import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { RequestReferralReadButtons } from '../../../../elements/carePathButtons/RequestReferralReadButtons';
import { UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import { RequestMessageContainer } from '../../../episodes/RequestMessageContainer';
import { ProfessionalSummaryDataRead } from '../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { MedicationsRead } from '../../utils/v1/medications/MedicationsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { RequestThumbnails } from '../../utils/v1/RequestThumbnails';
import { SendRequestMessage } from '../../utils/v1/SendRequestMessage';
import { TxcsMedicationsList } from '../../utils/v1/TxcsMedicationsList';

export function TovV1RequestRead(props: ICustomControlRequestReadProps) {
    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => {
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    });
                }}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <ProfileImages request={props.request} />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <RequestThumbnails {...props} />

                <MedicationsRead
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    patientUsesMedication={props.request.patientUsesMedication}
                />
                <ProfessionalSummaryDataRead request={props.request} />
                <TxcsMedicationsList requestId={props.request.id} />

                {getReadCustomControlData(props.request.customControlData.voorgeschiedenis, 'Voorgeschiedenis')}
                {getReadCustomControlData(props.request.customControlData.co_morbiditeiten, 'Comorbiditeiten')}
                {getReadCustomControlData(props.request.customControlData.verwijsreden, 'Verwijsreden')}
                {getReadCustomControlData(props.request.customControlData.toelichting, 'Toelichting')}

                {props.process.requestMessages && props.process.requestMessages.length > 0 && (
                    <RequestMessageContainer requestMessages={props.process.requestMessages} />
                )}

                <SendRequestMessage
                    isLastItem={props.isLastItem}
                    reloadAll={() => props.reloadProcess(ReloadEpisode.reloadAll)}
                    request={props.request}
                />

                <RequestReferralReadButtons {...props} />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideReferral')}
                    labelMore={translateKey('request.showReferral')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );
}

function getReadCustomControlData(value: string | null, label: string) {
    if (!value) {
        return null;
    }

    return (
        <HorizontalFormGroupRead>
            <FormLabel>{label}</FormLabel>
            <InputContainer>
                <UserTextParagraph>{value}</UserTextParagraph>
            </InputContainer>
        </HorizontalFormGroupRead>
    );
}
